import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  AlertTriangle,
  Eye,
  EyeOff,
  Lock,
  LockOpen,
  Mail,
  RotateCcw,
  ShieldCheck,
  Smartphone,
} from "lucide-react";
import * as React from "react";

import {
  Badge,
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@ando/ui";

import { type AdminUser, unlockZitadelAccount } from "@ando/core";

import { useHasCapability } from "@/features/capabilities";
import { formatDate, formatDateTime } from "@/lib/datetime";
import { useAdminUserTimeline, useZitadelAccountState } from "../hooks/useAdmin";
import { hashSeed, initialsFor, maskEmail, maskName, maskPhone } from "../lib/piiMask";
import { ZitadelPasswordActions } from "./ZitadelPasswordActions";
import { ROLE_CHOICES, RoleIcon, useUserAdminActions } from "./userAdminActions";

/**
 * UserDetailContent — the shared, chrome-agnostic body of the admin user
 * detail (Overview / Sessioni / MFA / Trace recenti / PII Inspect).
 *
 * Two hosts compose these pieces:
 *   - ``UserDetailShell`` — the full-width inline destination mounted at
 *     ``/admin/users/$userId`` (mirrors ``OrgDetailShell``), URL-driven
 *     ``?tab=`` tabs.
 *   - ``UserDetailDrawer`` — the cross-cutting right-side drill-down opened
 *     from inside the Org detail's Members tab, with local tab state.
 *
 * Both share the SAME tab triggers, tab panels and Inspect-Mode state so
 * behaviour (elevation, audit trail, session revoke, MFA, PII reveal) is
 * defined exactly once. The chrome (page header vs. Drawer portal) is the
 * only thing that differs between hosts.
 *
 * PII Inspect Mode security contract (v2 admin, May 2026):
 *   1. Capability-gated FRONT-END — the "PII Inspect" tab renders ONLY when
 *      ``useHasCapability("pii_inspect")`` is true. The BE additionally
 *      enforces ``require_platform_admin`` on every Inspect Mode mutation,
 *      so this is defense-in-depth.
 *   2. Audit log on activation — activating writes an "Elevation requested"
 *      line to the audit footer.
 *   3. Auto-revoke timer — 5-minute countdown; revocation is enforced by a
 *      ``setInterval`` tick AND a ``Date.now()`` re-check on every tick so a
 *      clock skew can't extend the window.
 */

// ─── Tab identity (URL contract for the shell) ──────────────────────────

/**
 * Tab ids exposed via the shell URL (``?tab=...``). Importing this tuple in
 * the route's ``validateSearch`` avoids a forked source of truth. Adding a
 * tab = one entry here. DRY.
 */
export const USER_DETAIL_TAB_IDS = ["overview", "sessions", "mfa", "traces", "pii"] as const;

export type UserDetailTabId = (typeof USER_DETAIL_TAB_IDS)[number];

const INSPECT_TTL_MS = 5 * 60_000;

export const USER_AVATAR_GRADIENTS = [
  "from-ando-orange-soft to-cream-deep",
  "from-success/30 to-cream-deep",
  "from-warning/30 to-cream-deep",
  "from-accent/30 to-cream-deep",
  "from-cream-deep to-ando-orange-soft",
] as const;

// ─── Inspect Mode state (shared hook) ───────────────────────────────────

export interface InspectMode {
  readonly inspectActive: boolean;
  readonly inspectExpiresAt: number | null;
  readonly now: number;
  readonly auditLog: string[];
  readonly activate: (reason: string) => void;
  readonly revoke: () => void;
}

/**
 * Owns the 5-minute PII unlock window + its audit trail. ``resetKey`` (the
 * user id) clears the window and log whenever the inspected subject changes
 * so an elevation never leaks across users.
 */
export function useInspectMode(resetKey: string | null): InspectMode {
  const { i18n } = useLingui();
  const [auditLog, setAuditLog] = React.useState<string[]>([]);
  const [inspectExpiresAt, setInspectExpiresAt] = React.useState<number | null>(null);
  const [now, setNow] = React.useState<number>(() => Date.now());

  const pushAudit = React.useCallback(
    (line: string): void => {
      const ts = new Intl.DateTimeFormat(i18n.locale, {
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date());
      setAuditLog((prev) => [...prev, `${ts} · ${line}`]);
    },
    [i18n.locale],
  );

  // Reset the window + trail when the inspected subject changes.
  React.useEffect(() => {
    setAuditLog([]);
    setInspectExpiresAt(null);
  }, [resetKey]);

  // 1s ticker — only runs while a window is open.
  React.useEffect(() => {
    if (inspectExpiresAt === null) return;
    const id = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(id);
  }, [inspectExpiresAt]);

  // Auto-revoke on TTL expiry, re-checked every tick so a clock skew can't
  // silently extend the window.
  React.useEffect(() => {
    if (inspectExpiresAt !== null && now >= inspectExpiresAt) {
      setInspectExpiresAt(null);
      pushAudit(i18n._(msg`Inspect Mode scaduta automaticamente (TTL 5 minuti)`));
    }
  }, [now, inspectExpiresAt, pushAudit, i18n]);

  const activate = React.useCallback(
    (reason: string): void => {
      setInspectExpiresAt(Date.now() + INSPECT_TTL_MS);
      pushAudit(i18n._(msg`Elevation requested · reason: ${reason}`));
    },
    [pushAudit, i18n],
  );

  const revoke = React.useCallback((): void => {
    if (inspectExpiresAt === null) return;
    setInspectExpiresAt(null);
    pushAudit(i18n._(msg`Inspect Mode revocata manualmente`));
  }, [inspectExpiresAt, pushAudit, i18n]);

  const inspectActive = inspectExpiresAt !== null && inspectExpiresAt > now;
  return { inspectActive, inspectExpiresAt, now, auditLog, activate, revoke };
}

// ─── Header card ────────────────────────────────────────────────────────

export function UserHeaderCard(props: {
  user: AdminUser | null;
  userId: string | null;
  inspectActive: boolean;
}): React.ReactElement {
  const { user, userId, inspectActive } = props;
  const initials = initialsFor(user?.display_name, user?.email);
  const seed = user?.user_id ?? userId ?? "unknown";
  const gradient = USER_AVATAR_GRADIENTS[hashSeed(seed, USER_AVATAR_GRADIENTS.length)]!;
  const displayName = inspectActive ? (user?.display_name ?? "—") : maskName(user?.display_name);
  const email = inspectActive ? (user?.email ?? "—") : maskEmail(user?.email);

  return (
    <div className="flex items-center gap-3">
      <div
        aria-hidden
        className={`h-12 w-12 shrink-0 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center font-medium text-ink-1 text-sm`}
      >
        {initials}
      </div>
      <div className="min-w-0">
        <p className="truncate font-medium text-ink-1">{displayName}</p>
        <p className="truncate text-2xs text-ink-3">{email}</p>
        <p className="mt-0.5 font-mono text-3xs text-ink-3">{seed}</p>
      </div>
    </div>
  );
}

// ─── Inspect Banner ──────────────────────────────────────────────────────

export function InspectBanner(props: {
  expiresAt: number;
  now: number;
  onRevoke: () => void;
}): React.ReactElement {
  const remaining = Math.max(0, props.expiresAt - props.now);
  const m = Math.floor(remaining / 60_000);
  const s = Math.floor((remaining % 60_000) / 1_000);

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center justify-between gap-2 border-b-2 border-b-ando-orange/55 bg-card px-5 py-2 text-ink-1 text-xs"
    >
      <span className="flex items-center gap-1.5">
        <ShieldCheck size={13} aria-hidden className="text-accent" />
        <Trans>
          Modalità Ispezione PII attiva · scade tra{" "}
          <strong className="tabular-nums">
            {m}m {String(s).padStart(2, "0")}s
          </strong>
        </Trans>
      </span>
      <Button size="sm" variant="ghost" className="min-h-[36px]" onClick={props.onRevoke}>
        <Trans>Termina ora</Trans>
      </Button>
    </div>
  );
}

// ─── Audit Footer ────────────────────────────────────────────────────────

export function AuditFooter(props: {
  lines: string[];
}): React.ReactElement | null {
  const { i18n } = useLingui();
  if (props.lines.length === 0) return null;
  return (
    <div
      role="log"
      aria-label={i18n._(msg`Audit log Inspect Mode`)}
      className="border-hairline border-t bg-cream/30 px-5 py-2"
    >
      {props.lines.slice(-3).map((line, i) => (
        <p key={i} className="font-mono text-3xs text-ink-3 tabular-nums">
          {line}
        </p>
      ))}
    </div>
  );
}

// ─── Tab triggers ────────────────────────────────────────────────────────

/**
 * The tab strip. Rendered inside a host-owned ``<Tabs>`` so both the shell
 * (sticky bar) and drawer (inline) can position it differently while sharing
 * the SAME triggers + keyboard nav.
 */
export function UserDetailTabsList(props: {
  tab: UserDetailTabId;
  onTabChange: (t: UserDetailTabId) => void;
  className?: string;
}): React.ReactElement {
  const piiInspectAllowed = useHasCapability("pii_inspect");
  return (
    <TabsList
      className={props.className ?? "w-full justify-start"}
      onKeyDown={(e) => handleTabsKeyDown(e, props.tab, props.onTabChange, piiInspectAllowed)}
    >
      <TabsTrigger value="overview" className="text-xs">
        <Trans>Overview</Trans>
      </TabsTrigger>
      <TabsTrigger value="sessions" className="text-xs">
        <Trans>Sessioni</Trans>
      </TabsTrigger>
      <TabsTrigger value="mfa" className="text-xs">
        MFA
      </TabsTrigger>
      <TabsTrigger value="traces" className="text-xs">
        <Trans>Trace recenti</Trans>
      </TabsTrigger>
      {piiInspectAllowed ? (
        <TabsTrigger value="pii" className="text-xs">
          <Trans>PII Inspect</Trans>
        </TabsTrigger>
      ) : null}
    </TabsList>
  );
}

// ─── Tab panels ──────────────────────────────────────────────────────────

/**
 * The 5 tab panels. Rendered inside a host-owned ``<Tabs>``; siblings with
 * ``UserDetailTabsList`` so the Radix context flows through.
 */
export function UserDetailTabsPanels(props: {
  user: AdminUser | null;
  userId: string | null;
  inspect: InspectMode;
}): React.ReactElement {
  const piiInspectAllowed = useHasCapability("pii_inspect");
  const { user, userId, inspect } = props;
  return (
    <>
      <TabsContent value="overview">
        <OverviewTab user={user} userId={userId} inspectActive={inspect.inspectActive} />
      </TabsContent>
      <TabsContent value="sessions">
        <SessionsTab user={user} />
      </TabsContent>
      <TabsContent value="mfa">
        <MfaTab user={user} />
      </TabsContent>
      <TabsContent value="traces">
        <TracesTab userId={userId} />
      </TabsContent>
      {piiInspectAllowed ? (
        <TabsContent value="pii">
          <PiiInspectTab
            user={user}
            inspectActive={inspect.inspectActive}
            onActivate={inspect.activate}
            onRevoke={inspect.revoke}
          />
        </TabsContent>
      ) : null}
    </>
  );
}

/**
 * Convenience: a fully stacked Tabs block (list + panels) for hosts that
 * don't need a sticky tab bar (the Drawer). The shell composes the list +
 * panels itself to place the bar in a sticky header.
 */
export function UserDetailStackedTabs(props: {
  user: AdminUser | null;
  userId: string | null;
  tab: UserDetailTabId;
  onTabChange: (t: UserDetailTabId) => void;
  inspect: InspectMode;
}): React.ReactElement {
  return (
    <Tabs
      value={props.tab}
      onValueChange={(v) => props.onTabChange(v as UserDetailTabId)}
      className="flex flex-col gap-4"
    >
      <UserDetailTabsList tab={props.tab} onTabChange={props.onTabChange} />
      <UserDetailTabsPanels user={props.user} userId={props.userId} inspect={props.inspect} />
    </Tabs>
  );
}

// ─── Overview Tab ────────────────────────────────────────────────────────

function OverviewTab(props: {
  user: AdminUser | null;
  userId: string | null;
  inspectActive: boolean;
}): React.ReactElement {
  const { user } = props;
  const { i18n } = useLingui();
  const actions = useUserAdminActions();
  const timeline = useAdminUserTimeline({ userId: props.userId });
  const kpis = timeline.data?.kpis ?? [];

  return (
    <div className="flex flex-col gap-4">
      {/* Profile card */}
      <section
        aria-labelledby="overview-profile"
        className="rounded-md border border-hairline bg-card/55 p-4"
      >
        <h3
          id="overview-profile"
          className="mb-2 font-medium text-3xs text-ink-3 uppercase tracking-wide"
        >
          <Trans>Profilo</Trans>
        </h3>
        {/* Colonna etichette su ``auto``: con due colonne uguali il valore
            partiva dal 50% della card. */}
        <dl className="grid grid-cols-1 gap-x-10 gap-y-3 sm:grid-cols-2 xl:grid-cols-4">
          <Pair
            label={i18n._(msg`Email`)}
            value={props.inspectActive ? (user?.email ?? "—") : maskEmail(user?.email)}
          />
          <Pair label={i18n._(msg`Tier`)} value={user?.tier ?? "—"} />
          <Pair label={i18n._(msg`Creato il`)} value={formatDate(i18n.locale, user?.created_at)} />
          <Pair
            label={i18n._(msg`Ultimo login`)}
            value={formatDateTime(i18n.locale, user?.last_login)}
          />
        </dl>

        {/* Erano lette qui e cambiate in un altro tab. */}
        <div className="mt-3 divide-y divide-hairline border-hairline border-t">
          <AccessRow label={i18n._(msg`Cambia stato`)}>
            {!user ? (
              <span className="text-ink-3">—</span>
            ) : user.status === "deleted" ? (
              // ``deleted`` il BE lo riporta ma non lo si imposta: nessuna
              // rotta lo scrive, quindi resta di sola lettura.
              <span className={cn("font-medium capitalize", statusTextClass(user.status))}>
                {user.status}
              </span>
            ) : (
              STATUS_CHOICES.map((c) => {
                const current = c.value === user.status;
                const danger = c.value === "suspended";
                return (
                  <button
                    key={c.value}
                    type="button"
                    aria-current={current ? "true" : undefined}
                    onClick={() =>
                      danger ? actions.requestSuspend(user) : actions.requestActivate(user)
                    }
                    className={cn(
                      OPTION_CLS,
                      current && danger && "cursor-default bg-rose-50 font-medium text-rose-700",
                      current &&
                        !danger &&
                        "cursor-default bg-ando-orange-soft font-medium text-ink-1",
                      !current && danger && "text-ink-2 hover:bg-rose-50 hover:text-rose-700",
                      !current && !danger && "text-ink-2 hover:bg-cream-deep hover:text-ink-1",
                    )}
                  >
                    {i18n._(c.label)}
                  </button>
                );
              })
            )}
          </AccessRow>

          {/* Voci esplicite invece di una tendina: si vede quali ruoli
              esistono e quale e' quello attuale senza aprire niente. Le
              promozioni passano comunque dalla conferma. */}
          <AccessRow label={i18n._(msg`Cambia ruolo`)}>
            {user ? (
              ROLE_CHOICES.map((r) => {
                const current = r.value === user.role;
                return (
                  <button
                    key={r.value}
                    type="button"
                    aria-current={current ? "true" : undefined}
                    onClick={() => actions.requestRoleChange(user, r.value)}
                    className={cn(
                      OPTION_CLS,
                      current
                        ? "cursor-default bg-ando-orange-soft font-medium text-ink-1"
                        : "text-ink-2 hover:bg-cream-deep hover:text-ink-1",
                    )}
                  >
                    {r.value === "admin" || r.value === "superadmin" ? (
                      <RoleIcon role={r.value} />
                    ) : null}
                    {i18n._(r.label)}
                  </button>
                );
              })
            ) : (
              <span className="text-ink-3">—</span>
            )}
          </AccessRow>
        </div>
      </section>

      {/* KPI strip */}
      <section aria-labelledby="overview-kpi">
        <h3
          id="overview-kpi"
          className="mb-2 font-medium text-3xs text-ink-3 uppercase tracking-wide"
        >
          <Trans>KPI periodo</Trans>
        </h3>
        {timeline.isLoading ? (
          <p className="text-2xs text-ink-3">
            <Trans>Caricamento KPI…</Trans>
          </p>
        ) : kpis.length === 0 ? (
          <p className="text-2xs text-ink-3">
            <Trans>Nessun KPI per il periodo.</Trans>
          </p>
        ) : (
          /* ``basis`` li tiene compatti a qualunque numero di KPI. */
          <ul className="flex flex-wrap gap-2">
            {kpis.slice(0, 4).map((k) => (
              <li
                key={k.label}
                className="flex-1 basis-[150px] rounded-md border border-hairline bg-card/55 px-3 py-2 sm:max-w-[220px]"
              >
                <p className="text-3xs text-ink-3 uppercase tracking-wide">{k.label}</p>
                <p className="font-semibold text-ink-1 text-lg tabular-nums leading-none">
                  {k.value.toFixed(0)}
                  <span className="ml-1 font-normal text-3xs text-ink-3">{k.unit}</span>
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      {actions.dialogs}
    </div>
  );
}

// Le due sole transizioni che il BE espone: ``activateUser`` e
// ``suspendUser``. ``deleted`` non e' scrivibile.
const STATUS_CHOICES = [
  { value: "active", label: msg`Attivo` },
  { value: "suspended", label: msg`Sospeso` },
] as const;

// Forma condivisa dalle opzioni di stato e di ruolo: quello che cambia e' solo
// il riempimento dello stato corrente.
//
// L'altezza la fa il padding, non un ``min-height``: con il line box ereditato
// a 1.7 il riquadro veniva 36px attorno a 9px di cap height, cioe' 13,5px sopra
// e sotto contro 12px ai lati, e la pill si leggeva gonfia in verticale.
// ``leading-none`` rende l'altezza calcolabile (12,5 + 2x6 = 24,5px) e porta il
// rapporto orizzontale/verticale a 2:1. Il touch target torna a 36px con lo
// pseudo-elemento, fuori dal riquadro visibile.
const OPTION_CLS =
  "relative inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs leading-none transition-colors after:absolute after:inset-x-0 after:-inset-y-[6px] after:content-[''] focus-visible:outline-none";

/** Etichetta sopra il valore: la prossimita' verticale e' il legame piu' forte,
 *  e la distinzione la fanno dimensione e maiuscoletto, non il peso. In riga
 *  lo spazio fra etichetta e valore finiva per eguagliare quello fra una
 *  coppia e l'altra. */
function Pair(props: { label: string; value: string }): React.ReactElement {
  return (
    <div className="min-w-0">
      <dt className="font-semibold text-3xs text-ink-3 uppercase tracking-wide">{props.label}</dt>
      <dd className="mt-0.5 truncate text-ink-1 text-xs">{props.value}</dd>
    </div>
  );
}

// ─── Sessions Tab (Zitadel state + BE-gap "sessions list") ──────────────

/** Etichetta a larghezza fissa, valore e azioni subito accanto: non si
 *  allontanano allargando la finestra. */
function AccessRow(props: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="grid grid-cols-[minmax(0,8.5rem)_1fr] items-center gap-x-6 py-2.5">
      <span className="font-semibold text-3xs text-ink-3 uppercase tracking-wide">
        {props.label}
      </span>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3 text-xs">{props.children}</div>
    </div>
  );
}

/** Il caso normale resta neutro: si colora solo cio' che merita
 *  attenzione. */
function statusTextClass(status: string): string {
  switch (status) {
    case "suspended":
      return "text-warning-foreground";
    case "deleted":
      return "text-destructive";
    default:
      return "text-ink-1";
  }
}

function SessionsTab(props: { user: AdminUser | null }): React.ReactElement {
  const { i18n } = useLingui();
  const state = useZitadelAccountState(props.user?.user_id ?? null);
  const data = state.data;
  const user = props.user;

  return (
    <div className="flex flex-col gap-3">
      {/* Stato applicativo e stato del provider sono due righe della stessa
          tabella, ognuna con accanto le leve che la riguardano. */}
      <section
        aria-labelledby="sessions-access"
        className="rounded-md border border-hairline bg-card/55 p-4 text-xs"
      >
        <h3
          id="sessions-access"
          className="mb-1 font-medium text-3xs text-ink-3 uppercase tracking-wide"
        >
          <Trans>Autenticazione</Trans>
        </h3>

        <div className="divide-y divide-hairline">
          <AccessRow label="Auth · Zitadel">
            {state.isLoading ? (
              <span className="text-ink-3">
                <Trans>Caricamento…</Trans>
              </span>
            ) : state.isError ? (
              <span className="text-destructive">
                <Trans>Stato Zitadel non disponibile</Trans>
              </span>
            ) : data ? (
              <>
                <span
                  className={cn(
                    "inline-flex items-center gap-1 font-medium capitalize",
                    data.is_locked ? "text-destructive" : "text-ink-1",
                  )}
                >
                  {data.is_locked ? <Lock size={11} aria-hidden /> : null}
                  {data.state}
                </span>
                {data.is_locked ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={async () => {
                      if (!user) return;
                      await unlockZitadelAccount(user.user_id);
                      void state.refetch();
                    }}
                    className="min-h-[36px] shadow-none hover:shadow-none"
                  >
                    <LockOpen size={12} className="mr-1" aria-hidden />
                    <Trans>Sblocca</Trans>
                  </Button>
                ) : null}
                {user && data.zitadel_user_id ? (
                  <ZitadelPasswordActions userId={user.user_id} />
                ) : null}
              </>
            ) : (
              <span className="text-ink-3">—</span>
            )}
          </AccessRow>
        </div>

        {/* Visibili e disabilitate: sono il promemoria di cosa manca lato BE
            (``resetUserSessions`` & co. lanciano ``BE_GAP``). L'etichetta lo
            dice una volta sola: su un item disabilitato un tooltip non si
            aprirebbe comunque (``pointer-events-none``). */}
        <div className="mt-1 border-hairline border-t pt-3">
          <p className="mb-2 font-medium text-3xs text-ink-3 uppercase tracking-wide">
            <Trans>In arrivo</Trans>
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Button
              size="sm"
              variant="secondary"
              disabled
              className="min-h-[36px] shadow-none hover:shadow-none"
            >
              <RotateCcw size={12} className="mr-1" aria-hidden />
              <Trans>Resetta sessioni</Trans>
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled
              className="min-h-[36px] shadow-none hover:shadow-none"
            >
              <Smartphone size={12} className="mr-1" aria-hidden />
              <Trans>Forza enrolment MFA</Trans>
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled
              className="min-h-[36px] shadow-none hover:shadow-none"
            >
              <Mail size={12} className="mr-1" aria-hidden />
              <Trans>Reinvia email di verifica</Trans>
            </Button>
          </div>
        </div>
      </section>

      <BEGapNotice
        label={i18n._(msg`Lista sessioni attive`)}
        description={i18n._(
          msg`Tabella device / IP / geo / started_at — endpoint /admin/users/{id}/sessions in roadmap.`,
        )}
      />
    </div>
  );
}

function MfaTab(props: { user: AdminUser | null }): React.ReactElement {
  const { i18n } = useLingui();
  void props;
  return (
    <div className="flex flex-col gap-3">
      <section
        aria-labelledby="mfa-state"
        className="rounded-md border border-hairline bg-card/55 p-3 text-xs"
      >
        <h3 id="mfa-state" className="mb-2 font-medium text-3xs text-ink-3 uppercase tracking-wide">
          <Trans>Stato MFA</Trans>
        </h3>
        <ul className="space-y-1.5">
          <li className="flex items-center gap-2">
            <Smartphone size={12} className="text-ink-3" aria-hidden />
            <span className="text-ink-2">
              <Trans>TOTP authenticator</Trans>
            </span>
            <Badge variant="secondary" className="ml-auto text-3xs">
              <Trans>n/d (BE)</Trans>
            </Badge>
          </li>
          <li className="flex items-center gap-2">
            <Mail size={12} className="text-ink-3" aria-hidden />
            <span className="text-ink-2">
              <Trans>OTP via email</Trans>
            </span>
            <Badge variant="secondary" className="ml-auto text-3xs">
              <Trans>n/d (BE)</Trans>
            </Badge>
          </li>
          <li className="flex items-center gap-2">
            <ShieldCheck size={12} className="text-ink-3" aria-hidden />
            <span className="text-ink-2">
              <Trans>Recovery codes</Trans>
            </span>
            <Badge variant="secondary" className="ml-auto text-3xs">
              <Trans>n/d (BE)</Trans>
            </Badge>
          </li>
        </ul>
      </section>
      <BEGapNotice
        label={i18n._(msg`Forza reset MFA`)}
        description={i18n._(msg`POST /admin/users/{id}/force-mfa — endpoint non ancora esposto.`)}
      />
    </div>
  );
}

// ─── Traces Tab ──────────────────────────────────────────────────────────

function TracesTab(props: { userId: string | null }): React.ReactElement {
  const { i18n } = useLingui();
  const timeline = useAdminUserTimeline({ userId: props.userId });
  const events = (timeline.data?.timeline ?? []).slice(0, 20);

  if (timeline.isLoading) {
    return (
      <p className="text-2xs text-ink-3">
        <Trans>Caricamento trace…</Trans>
      </p>
    );
  }
  if (events.length === 0) {
    return (
      <p className="text-2xs text-ink-3">
        <Trans>Nessuna attività nel periodo.</Trans>
      </p>
    );
  }
  return (
    <ul className="divide-y divide-hairline rounded-md border border-hairline bg-card/55">
      {events.map((e, idx) => (
        <li key={idx}>
          <button
            type="button"
            onClick={() =>
              console.warn(
                "[admin-users] trace detail modal not yet wired — defer to next stream",
                e,
              )
            }
            className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-cream/30"
          >
            <Badge variant="outline" className="shrink-0 text-3xs">
              {e.kind}
            </Badge>
            <div className="min-w-0 flex-1">
              <p className="truncate text-ink-1 text-xs">{e.summary}</p>
              <p className="text-3xs text-ink-3 tabular-nums">
                {formatDateTime(i18n.locale, e.timestamp)}
              </p>
            </div>
            {e.cost_usd != null ? (
              <span className="text-3xs text-ink-3 tabular-nums">${e.cost_usd.toFixed(3)}</span>
            ) : null}
          </button>
        </li>
      ))}
    </ul>
  );
}

// ─── PII Inspect Tab ─────────────────────────────────────────────────────

function PiiInspectTab(props: {
  user: AdminUser | null;
  inspectActive: boolean;
  onActivate: (reason: string) => void;
  onRevoke: () => void;
}): React.ReactElement {
  const { i18n } = useLingui();
  const [reason, setReason] = React.useState("");
  const reasonOk = reason.trim().length >= 10;
  const { user, inspectActive } = props;

  return (
    <div className="flex flex-col gap-4">
      <section
        aria-labelledby="pii-state"
        className="rounded-md border border-hairline bg-card/55 p-3 text-xs"
      >
        <h3
          id="pii-state"
          className="mb-2 flex items-center gap-2 font-medium text-3xs text-ink-3 uppercase tracking-wide"
        >
          {inspectActive ? (
            <Eye size={11} className="text-accent" aria-hidden />
          ) : (
            <EyeOff size={11} aria-hidden />
          )}
          <Trans>PII utente</Trans>
        </h3>
        <dl className="grid grid-cols-1 gap-x-10 gap-y-3 sm:grid-cols-2">
          <Pair
            label={i18n._(msg`Email`)}
            value={inspectActive ? (user?.email ?? "—") : maskEmail(user?.email)}
          />
          <Pair
            label={i18n._(msg`Nome completo`)}
            value={inspectActive ? (user?.display_name ?? "—") : maskName(user?.display_name)}
          />
          <Pair
            label={i18n._(msg`Telefono`)}
            // The BE does not yet return ``phone`` on /admin/users; once the
            // field ships, surface ``user.phone`` here. For now we render the
            // mask format for the operator to validate the contract visually.
            value={inspectActive ? "—" : maskPhone(null)}
          />
        </dl>
      </section>

      {inspectActive ? (
        <div className="flex items-center justify-between rounded-md border border-hairline border-l-2 border-l-ando-orange/55 bg-card/60 p-3 text-xs">
          <span>
            <Trans>Modalità Ispezione attiva</Trans>
          </span>
          <Button size="sm" variant="ghost" onClick={props.onRevoke} className="min-h-[36px]">
            <Trans>Termina ora</Trans>
          </Button>
        </div>
      ) : (
        <section
          aria-labelledby="pii-elevate"
          className="rounded-md border border-warning/30 bg-warning/5 p-3 text-xs"
        >
          <h3
            id="pii-elevate"
            className="mb-2 flex items-center gap-1.5 font-medium text-2xs text-ink-1"
          >
            <AlertTriangle size={12} className="text-warning" aria-hidden />
            <Trans>Elevation richiesta</Trans>
          </h3>
          <p className="mb-2 text-ink-2">
            <Trans>
              Per visualizzare email, nome e telefono in chiaro, fornisci una giustificazione (≥ 10
              caratteri). L'azione è registrata in audit log e la modalità scade automaticamente
              dopo 5 minuti.
            </Trans>
          </p>
          <label className="block">
            <span className="sr-only">
              <Trans>Motivo elevation PII</Trans>
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              minLength={10}
              required
              aria-label={i18n._(msg`Motivo elevation PII`)}
              placeholder={i18n._(msg`Es. richiesta DSAR ticket #4598 — verifica identità utente`)}
              className="w-full rounded-md border border-hairline bg-cream/30 p-2 text-ink-1 text-xs focus:outline-none"
            />
          </label>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-3xs text-ink-3 tabular-nums">{reason.trim().length}/10</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    size="sm"
                    onClick={() => props.onActivate(reason.trim())}
                    disabled={!reasonOk}
                    className="min-h-[36px]"
                  >
                    <Trans>Attiva Inspect Mode (5m)</Trans>
                  </Button>
                </span>
              </TooltipTrigger>
              {!reasonOk ? (
                <TooltipContent>
                  <Trans>Motivo obbligatorio (≥ 10 caratteri)</Trans>
                </TooltipContent>
              ) : null}
            </Tooltip>
          </div>
        </section>
      )}
    </div>
  );
}

// ─── BE-gap notice ──────────────────────────────────────────────────────

function BEGapNotice(props: {
  label: string;
  description: string;
}): React.ReactElement {
  return (
    <div className="rounded-md border border-warning/30 bg-warning/5 p-3 text-ink-2 text-xs">
      <p className="mb-0.5 font-medium text-ink-1">{props.label}</p>
      <p className="text-2xs text-ink-3">{props.description}</p>
    </div>
  );
}

// ─── Tabs keyboard nav ──────────────────────────────────────────────────

function handleTabsKeyDown(
  e: React.KeyboardEvent<HTMLDivElement>,
  current: UserDetailTabId,
  set: (t: UserDetailTabId) => void,
  piiAllowed: boolean,
): void {
  const visible: readonly UserDetailTabId[] = piiAllowed
    ? USER_DETAIL_TAB_IDS
    : USER_DETAIL_TAB_IDS.filter((t) => t !== "pii");
  const idx = visible.indexOf(current);
  if (idx === -1) return;
  if (e.key === "ArrowRight") {
    e.preventDefault();
    set(visible[(idx + 1) % visible.length]!);
  } else if (e.key === "ArrowLeft") {
    e.preventDefault();
    set(visible[(idx - 1 + visible.length) % visible.length]!);
  }
}
